import { Form, Modal, notification, UploadFile } from "antd";
import React, { useEffect, useState } from "react";
import { submitCoverWizardData } from "../../../store/actions/orderFlowActionCreators";
import AdditionalHardwarePage from "../OrderContextMenu/CoverWizardModal/components/FormPages/AdditionalHardwarePage";
import JobInfoPage from "../OrderContextMenu/CoverWizardModal/components/FormPages/JobInfoPage";
import ShippingInformationPage, {
  AutofilledValue,
  AutofillRadio,
  useShippingInformationPageState,
} from "../OrderContextMenu/CoverWizardModal/components/FormPages/ShippingInformationPage";
import { CustomerAddressDto } from "../../../store/actions/dtos/CustomerAddressDto";
import { StateCode } from "../OrderContextMenu/CoverWizardModal/CoverWizardModal";
import {
  getCustomerAddresses,
  getCustomerInfo,
} from "../../../store/actions/customerActionCreators";
import {
  getHardware,
  submitHardwareOrder,
} from "../../../store/actions/hardwareCreators";
import CustomerInfoDto from "../../../store/actions/dtos/CustomerInfoDto";
import moment, { Moment } from "moment";
import LeaveWizardConfirmationModal from "../OrderContextMenu/CoverWizardModal/components/LeaveWizardConfirmationModal";
import {
  SubmittedOrderModal,
  SubmittedOrderModalPayload,
} from "../OrderContextMenu/CoverWizardModal/components/SubmittedOrderModal";
import { getStateList } from "../../../store/actions/adminActionCreators";
import HardwarePage from "./HardwarePage";
import { PricedHardwareItemDto } from "../../../store/actions/dtos/PricedHardwareItemDto";

export type SubmitHardwareOrderDto = {
  quoteType: string | null;
  hardware: {
    item: string;
    quantity: number;
  }[];
  date: string;
  poNumber: string;
  jobName: string;
  poolOwnerInfo: {
    address: string;
    city: string;
    stateCode: string | null;
    zip: string;
  };
  shippingInfo: {
    shippingName: string;
    shippingAddress: string;
    shippingCity: string;
    shippingStateCode: string | null;
    shippingZip: string;
  };
};

export type CreateHardwareOrderForm = {
  date: Moment;
  poNumber: string;
  jobName: string;
  address: string;
  city: string;
  stateCode: string | null;
  zip: string;
  comments: string;
  quoteType: string;
  shippingName: string;
  shippingAddress: string;
  shippingCity: string;
  shippingStateCode: string | null;
  shippingZip: string;
  additionalHardware: string[];
  pricedHardwareItems: PricedHardwareItemDto[];
};

function formModelToPayload(
  valuesToSubmit: CreateHardwareOrderForm
): SubmitHardwareOrderDto {
  return {
    quoteType: valuesToSubmit.quoteType,
    hardware: valuesToSubmit.pricedHardwareItems.map((item) => ({
      item: item.item,
      quantity: item.quantity,
    })),
    date: new Date().toUTCString(),
    poNumber: valuesToSubmit.poNumber,
    jobName: valuesToSubmit.jobName,
    poolOwnerInfo: {
      address: valuesToSubmit.address,
      city: valuesToSubmit.city,
      stateCode: valuesToSubmit.stateCode,
      zip: valuesToSubmit.zip,
    },
    shippingInfo: {
      shippingName: valuesToSubmit.shippingName,
      shippingAddress: valuesToSubmit.shippingAddress,
      shippingCity: valuesToSubmit.shippingCity,
      shippingStateCode: valuesToSubmit.shippingStateCode,
      shippingZip: valuesToSubmit.shippingZip,
    },
  };
}

export const emptyCreateHardwareOrderForm: CreateHardwareOrderForm = {
  // Pool Owner Info Page
  date: moment(),
  poNumber: "",
  jobName: "",
  address: "",
  city: "",
  stateCode: null,
  zip: "",
  comments: "",
  quoteType: "Hardware Order",

  // Shipping Information Page
  shippingName: "",
  shippingAddress: "",
  shippingCity: "",
  shippingStateCode: null,
  shippingZip: "",

  // Additional Hardware Page
  additionalHardware: [],
  pricedHardwareItems: [],
};

export function CreateHardwareOrderModal({
  modalPayloadState: [modalPayload, setModalPayload],
  submittedModalPayloadState: [submittedModalPayload, setSubmittedModalPayload],
  onSubmit,
}: {
  modalPayloadState: [
    CreateHardwareOrderForm | null,
    React.Dispatch<React.SetStateAction<CreateHardwareOrderForm | null>>
  ];
  submittedModalPayloadState: [
    SubmittedOrderModalPayload | null,
    React.Dispatch<React.SetStateAction<SubmittedOrderModalPayload | null>>
  ];
  onSubmit: () => void;
}) {
  const [leaveWizardModalOpen, setLeaveWizardModalOpen] =
    useState<boolean>(false);
  const [form] = Form.useForm<CreateHardwareOrderForm>();
  const [wizardPageNumber, setWizardPageNumber] = useState<number>(1);
  const [formIsSubmitting, setFormIsSubmitting] = useState<boolean>(false);
  const [otherHardware, setOtherHardware] = useState<string[] | null>(null);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfoDto | null>(
    null
  );
  const [extraLongWallAnchors, setExtraLongWallAnchors] = useState<
    string[] | null
  >(null);
  const [customerAddresses, setCustomerAddresses] = useState<
    CustomerAddressDto[] | null
  >(null);
  const [usStateOptions, setUsStateOptions] = useState<StateCode[]>([]);

  //State moved here to ensure the page can resume from where it left off
  const shippingPageState = useShippingInformationPageState();

  async function _getCustomerAddresses() {
    if (!customerAddresses) {
      let [response, err] = await getCustomerAddresses();
      if (response?.statusCode === 200) {
        setCustomerAddresses(response.data);
        return response.data;
      }
      if (err) {
        notification.open({
          message: "Failed to get customer addresses",
        });
      }
    } else {
      return customerAddresses;
    }
  }

  async function _getHardware() {
    let [response, err] = await getHardware();
    if (response?.statusCode === 200) {
      const _otherHardware: string[] = [];
      const _extraLongWallAnchors: string[] = [];
      response.data.forEach((item) => {
        if (item.includes("EXTRA LONG WALL ANCHOR")) {
          _extraLongWallAnchors.push(item);
        } else {
          _otherHardware.push(item);
        }
      });
      setOtherHardware(_otherHardware);
      setExtraLongWallAnchors(_extraLongWallAnchors);
      return response.data;
    }
    if (err) {
      notification.open({
        message: "Failed to get hardware",
      });
    }
  }

  async function _getCustomerInfo() {
    if (!customerInfo) {
      let [response, err] = await getCustomerInfo(0);
      if (response?.status === 200) {
        setCustomerInfo(response.data.data);
        return response.data.data;
      }
      if (err) {
        notification.open({
          message: "Failed to get customer info",
        });
      }
    } else {
      return customerInfo;
    }
  }

  useEffect(() => {
    fetchStateList();
  }, []);

  async function fetchStateList() {
    let [response, err]: Array<any> = await getStateList();
    if (response) {
      if (response.status === 200) {
        setUsStateOptions(response.data.data);
      }
    }
    if (err) {
      setUsStateOptions([]);
    }
  }

  function handleCloseModal() {
    setLeaveWizardModalOpen(false);
    form.resetFields();
    setWizardPageNumber(1);
    setModalPayload(null);
  }

  return (
    <>
      <Modal
        open={!!modalPayload}
        footer={null}
        centered
        maskClosable={false}
        closable={!formIsSubmitting}
        width={560}
        style={{ maxHeight: "97vh" }}
        keyboard={false}
        onCancel={() => {
          setLeaveWizardModalOpen(true);
        }}
      >
        <Form
          form={form}
          preserve
          layout="vertical"
          colon={false}
          name="hardwareQuoteForm"
          initialValues={modalPayload ?? emptyCreateHardwareOrderForm}
          onFinish={async (_: CreateHardwareOrderForm) => {
            const values = form.getFieldsValue(true);
            setFormIsSubmitting(true);
            const [submitCoverWizardRes, submitCoverWizardError] =
              await submitHardwareOrder(formModelToPayload(values));
            if (!!submitCoverWizardError || !submitCoverWizardRes) {
              notification.error({
                message: "Error submitting form",
                description:
                  "There was an error submitting your form, please try again later.",
              });
              setFormIsSubmitting(false);
              return;
            }
            onSubmit?.();
            setFormIsSubmitting(false);
            setSubmittedModalPayload({
              soNumber: submitCoverWizardRes.data.toString() ?? "",
              jobName: values.jobName ?? "",
              isRga: false,
              formData: values, // Include the form data
            });
            handleCloseModal();
          }}
        >
          {wizardPageNumber === 1 ? (
            <JobInfoPage
              hideQuoteType
              hideBackButton
              onNextButtonClick={() => setWizardPageNumber(2)}
              usStateOptions={usStateOptions}
            />
          ) : wizardPageNumber === 2 ? (
            <ShippingInformationPage
              usStateOptions={usStateOptions}
              onNextButtonClick={() => setWizardPageNumber(3)}
              onBackButtonClick={() => setWizardPageNumber(1)}
              getCustomerInfo={_getCustomerInfo}
              getCustomerAddresses={_getCustomerAddresses}
              shippingInformationPageState={shippingPageState}
            />
          ) : wizardPageNumber === 3 ? (
            <HardwarePage
              submitOnNextButtonClick
              onBackButtonClick={() => setWizardPageNumber(2)}
              formIsSubmitting={formIsSubmitting}
              wizardPageNumberState={[wizardPageNumber, setWizardPageNumber]}
            />
          ) : null}
        </Form>
      </Modal>
      <LeaveWizardConfirmationModal
        modalIsOpenState={[leaveWizardModalOpen, setLeaveWizardModalOpen]}
        onProceed={handleCloseModal}
      />
      <SubmittedOrderModal
        submittedModalPayloadState={[
          submittedModalPayload,
          setSubmittedModalPayload,
        ]}
      />
    </>
  );
}
